import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../api/apiService';
import { getStoredRefreshToken, getStoredToken } from '../auth/auth';
import { isDevAuthBypassEnabled } from '../auth/devAuthBypass';
import { normalizeAnalyticProfilesResponse } from '../utils/analyticProfilesResponse';
import { UserContext } from './UserContext';

// Per-user UI preference (not shareable state, so it lives in localStorage rather than the URL).
const selectedProfileStorageKey = (userId: string) => `flock.selectedAnalyticProfile.${userId}`;

const readStoredProfileId = (userId: string): string | null => {
  try {
    return localStorage.getItem(selectedProfileStorageKey(userId));
  } catch {
    return null;
  }
};

const writeStoredProfileId = (userId: string, profileId: string) => {
  try {
    localStorage.setItem(selectedProfileStorageKey(userId), profileId);
  } catch {
    // Storage unavailable (private mode, quota); selection just won't persist.
  }
};

// Define interface for analytic profile
export interface AnalyticProfile {
  id: string;
  name: string;
  description?: string;
  keywords?: string[];
  /** Used for Content Radar lookback (converted to days for `lookback_days`). */
  time_window_hours?: number;
  created_at?: string;
  selected_language?: string;
}

// Define context type
export interface AnalyticProfileContextType {
  availableProfiles: AnalyticProfile[];
  selectedProfileId: string | null;
  selectedProfile: AnalyticProfile | null;
  isLoading: boolean;
  error: string | null;
  selectProfile: (profileId: string) => void;
  /**
   * Increments only on an explicit user choice (selectProfile), not on the initial default or the
   * stored-selection restore, so views can tell "the user switched profile" from "profile loaded".
   */
  selectionRevision: number;
  fetchProfiles: () => Promise<void>;
}

// Create context with default values
export const AnalyticProfileContext = createContext<AnalyticProfileContextType>({
  availableProfiles: [],
  selectedProfileId: null,
  selectedProfile: null,
  isLoading: true,
  error: null,
  selectProfile: () => {},
  selectionRevision: 0,
  fetchProfiles: async () => {},
});

// Define props for AnalyticProfileProvider
type AnalyticProfileProviderProps = {
  children: ReactNode;
};

export const AnalyticProfileProvider: React.FC<AnalyticProfileProviderProps> = ({ children }) => {
  const [availableProfiles, setAvailableProfiles] = useState<AnalyticProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const { user } = useContext(UserContext);
  const userId = user?.id ?? null;
  // fetchProfiles runs from a mount effect, so read the user through a ref to see the latest value.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // Which user the stored selection has been applied for (guards the late-login restore below).
  const restoredForUserRef = useRef<string | null>(null);

  // Function to fetch available analytic profiles
  const fetchProfiles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const hasAccess = Boolean(getStoredToken());
      const hasRefresh = Boolean(getStoredRefreshToken());
      const canFetch = hasAccess || hasRefresh || isDevAuthBypassEnabled();
      if (!canFetch) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      const raw = await api.get('/api/analytic-profiles');
      const profiles = normalizeAnalyticProfilesResponse(raw);
      const mappedProfiles = profiles.map((profile: any) => ({
        id: profile.id,
        name: profile.name,
        description: profile.description,
        keywords: profile.keywords,
        time_window_hours:
          typeof profile.time_window_hours === 'number' ? profile.time_window_hours : undefined,
        created_at: profile.created_at,
        selected_language: profile.selected_language,
      }));

      setAvailableProfiles(mappedProfiles);

      // Apply the stored selection in the same update as the list, so consumers never render the default first.
      const ids = new Set(mappedProfiles.map((p) => p.id));
      const currentUserId = userIdRef.current;
      const storedId = currentUserId ? readStoredProfileId(currentUserId) : null;
      if (currentUserId) {
        restoredForUserRef.current = currentUserId;
      }
      setSelectedProfileId((prev) => {
        if (mappedProfiles.length === 0) {
          return null;
        }
        if (prev && ids.has(prev)) {
          return prev;
        }
        return storedId && ids.has(storedId) ? storedId : mappedProfiles[0].id;
      });

      setError(null);
    } catch (error: any) {
      if (error?.status === 403) {
        // Backends before flock-be#52 gated this list on a permission; for the user that just means
        // no profiles. Safe to drop once every environment runs #52 or later.
        console.debug('Analytic profiles list forbidden (pre flock-be#52 backend); treating as none.');
        setError(null);
        setAvailableProfiles([]);
        setSelectedProfileId(null);
        return;
      }
      console.error('Error fetching analytic profiles:', error);
      setError(error.message || 'Failed to load analytic profiles');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to select a profile
  // Only explicit choices are persisted; the fallback default never overwrites a stored selection.
  const selectProfile = (profileId: string) => {
    setSelectedProfileId(profileId);
    setSelectionRevision((r) => r + 1);
    if (userIdRef.current) {
      writeStoredProfileId(userIdRef.current, profileId);
    }
  };

  // Fetch profiles when the provider mounts
  useEffect(() => {
    fetchProfiles();
  }, []);

  // On a fresh login the user can arrive after the profiles have loaded; restore for them then.
  useEffect(() => {
    if (!userId || availableProfiles.length === 0 || restoredForUserRef.current === userId) {
      return;
    }
    restoredForUserRef.current = userId;
    const storedId = readStoredProfileId(userId);
    if (storedId && availableProfiles.some((p) => p.id === storedId)) {
      setSelectedProfileId(storedId);
    }
  }, [userId, availableProfiles]);

  // Compute selected profile from available profiles
  const selectedProfile = selectedProfileId 
    ? availableProfiles.find(p => p.id === selectedProfileId) || null 
    : null;

  return (
    <AnalyticProfileContext.Provider
      value={{
        availableProfiles,
        selectedProfileId,
        selectedProfile,
        isLoading,
        error,
        selectProfile,
        selectionRevision,
        fetchProfiles
      }}
    >
      {children}
    </AnalyticProfileContext.Provider>
  );
};

export default AnalyticProfileProvider; 